import React, { useContext } from 'react';
import { Pressable, View } from 'react-native';
import { GroupChannelContexts } from '@sendbird/uikit-react-native';
import { TextInput } from '@sendbird/uikit-react-native-foundation';
import { useAuth } from 'app/auth/hooks/useAuth';
import { UploadDocumentModal } from 'app/_modules/history';
import { Text } from '@shared/components/text';
import { useI18n } from '@shared/i18n/useI18n';
import { CHAT_COMPOSER_INPUT_STYLE } from '../../constants';
import { useChatComposer } from '../../hooks/useChatComposer';
import { useChatUpload } from '../../hooks/useChatUpload';
import type { TChatComposerProps } from '../../types';
import { ChatAttachButton } from './ChatAttachButton';
import { ChatSendButton } from './ChatSendButton';

export const ChatComposer = ({
    shouldRenderInput,
    inputDisabled = false,
    onPressSendUserMessage,
    onPressSendFileMessage,
    onPressUpdateUserMessage,
    partialTextInputProps,
}: TChatComposerProps) => {
    const { t } = useI18n();
    const { channel, messageToEdit, setMessageToEdit } = useContext(GroupChannelContexts.Fragment);
    const { meData } = useAuth();
    const upload = useChatUpload(meData?.current_pathway?.id ?? '', onPressSendFileMessage);
    const composer = useChatComposer({
        channel,
        inputDisabled,
        messageToEdit,
        setMessageToEdit,
        onPressSendUserMessage,
        onPressUpdateUserMessage,
    });

    if (!shouldRenderInput) return null;

    return (
        <>
            <View className="flex-row items-end gap-2 px-md py-md bg-white border-t border-gray-200">
                <View className="flex-1 gap-2">
                    {composer.isEditing && (
                        <Pressable onPress={composer.cancelEdit} accessibilityRole="button" hitSlop={8}>
                            <Text className="text-sm font-semibold text-secondary-600">
                                {t('screens.chat.cancelEdit')}
                            </Text>
                        </Pressable>
                    )}
                    <View>
                        <TextInput
                            {...partialTextInputProps}
                            ref={composer.inputRef}
                            multiline
                            disableFullscreenUI
                            editable={!composer.disabled}
                            autoFocus={composer.autoFocus}
                            value={composer.text}
                            onChangeText={composer.setText}
                            placeholder={composer.placeholder}
                            style={CHAT_COMPOSER_INPUT_STYLE}
                        />
                        <ChatAttachButton
                            disabled={composer.disabled || upload.isUploading || composer.isEditing}
                            label={t('screens.chat.attachFile')}
                            onPress={() => upload.setIsUploadModalVisible(true)}
                        />
                    </View>
                </View>
                <ChatSendButton
                    enabled={composer.canSend}
                    label={t('screens.chat.sendMessage')}
                    onPress={composer.submit}
                />
            </View>

            <UploadDocumentModal
                visible={upload.isUploadModalVisible}
                onClose={() => upload.setIsUploadModalVisible(false)}
                onSelectCamera={upload.handleCameraUpload}
                onSelectExternalDevice={upload.handleExternalDeviceUpload}
            />
        </>
    );
};
